import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 10000,
});

// 添加请求拦截器，自动添加Authorization头
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    const currentTenant = localStorage.getItem('currentTenant') || 'default';
    config.headers['X-Tenant-Code'] = currentTenant;
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const getApiBase = (): string => {
  const currentTenant = localStorage.getItem('currentTenant') || 'default';
  return `/${currentTenant}`;
};

export interface Modeling {
  id: number;
  project_id: string;
  modeling_name: string;
  display_name: string;
  description?: string;
  version: string;
  is_active: boolean;
  tenant_code: string;
  created_at: string;
  updated_at: string;
}

export interface Model {
  id: number;
  model_id: string;
  display_name: string;
  reference_name: string;
  source_table_name: string;
  description?: string;
  category?: string;
  scope?: string;
  is_active: boolean;
  is_synchronized: boolean;
  modeling_id: number;
  created_at: string;
  updated_at: string;
}

export interface ModelField {
  id: number;
  field_name: string;
  display_name: string;
  description?: string;
  data_type: string;
  is_calculated: boolean;
  is_active: boolean;
  model_id: number;
  created_at: string;
  updated_at: string;
}

export interface ModelingWithDetails extends Modeling {
  models: Model[];
}

export const modelingService = {
  // 获取所有激活的建模
  async getActiveModelings(): Promise<Modeling[]> {
    const response = await api.get(`${getApiBase()}/modeling`);
    return response.data.data || [];
  },

  // 根据建模ID获取所有模型
  async getModelsByModeling(modelingId: number): Promise<Model[]> {
    const response = await api.get(`${getApiBase()}/modeling/${modelingId}/models`);
    return response.data.data || [];
  },
  /** 获取可用于创建模型的表列表（从 ChatBIAPIService），已建模型的表为 disabled */
  async getAvailableTablesForCreate(): Promise<{
    tables: Array<{
      name: string;
      disabled: boolean;
    }>;
  }> {
    const base = getApiBase();
    const response = await api.get(`${base}/remote_modeling/available_tables`);
    if (!response.data.success) throw new Error(response.data.message || '获取可用表失败');
    return response.data.data || { tables: [] };
  },

  /** 获取模型向量化同步状态（DeployStatus） */
  async getVectorSyncStatus(): Promise<{ status: string }> {
    const base = getApiBase();
    const response = await api.get(`${base}/remote_modeling/vector_sync_status`);
    if (!response.data.success) throw new Error(response.data.message || '获取同步状态失败');
    return response.data.data ?? { status: 'unknown' };
  },

  /** 执行模型向量化同步（Deploy） */
  async triggerVectorSync(): Promise<{ deploy: boolean }> {
    const base = getApiBase();
    const response = await api.post(`${base}/remote_modeling/vector_sync`);
    if (!response.data.success) throw new Error(response.data.message || '执行同步失败');
    return response.data.data ?? { deploy: false };
  },

  /** 根据表名获取表字段（从 ChatBIAPIService 的 GetDataSourceTable） */
  async getTableColumns(
    tableName: string
  ): Promise<{ name: string; columns: Array<{ name: string; type: string }> }> {
    const base = getApiBase();
    const response = await api.get(`${base}/remote_modeling/table_columns`, {
      params: { name: tableName }
    });
    if (!response.data.success) throw new Error(response.data.message || '获取表字段失败');
    return response.data.data;
  },

  // 获取所有模型（根据当前租户的激活建模）
  async getAllModels(): Promise<Model[]> {
    const response = await api.get(`${getApiBase()}/models`);
    return response.data.data || [];
  },

  // 创建模型
  async createModel(modelData: {
    model_id: string;
    display_name: string;
    reference_name: string;
    source_table_name: string;
    description?: string;
    category?: string;
    modeling_id: number;
  }): Promise<Model> {
    const response = await api.post(`${getApiBase()}/models`, {
      ...modelData,
      is_active: true,
      is_synchronized: false
    });
    return response.data.data;
  },

  // 更新模型
  async updateModel(modelId: number, updates: {
    model_id?: string;
    display_name?: string;
    reference_name?: string;
    source_table_name?: string;
    description?: string;
    category?: string;
    is_active?: boolean;
    is_synchronized?: boolean;
  }): Promise<void> {
    await api.put(`${getApiBase()}/models/${modelId}`, updates);
  },

  // 删除模型
  async deleteModel(modelId: number): Promise<void> {
    await api.delete(`${getApiBase()}/models/${modelId}`);
  },

  // 获取模型字段
  async getModelFields(modelId: number): Promise<ModelField[]> {
    const response = await api.get(`${getApiBase()}/models/${modelId}/fields`);
    return response.data.data || [];
  },

  // 创建模型字段
  async createModelField(fieldData: {
    field_name: string;
    display_name: string;
    description?: string;
    data_type: string;
    model_id: number;
    is_calculated?: boolean;
  }): Promise<ModelField> {
    const response = await api.post(`${getApiBase()}/model-fields`, {
      ...fieldData,
      is_calculated: fieldData.is_calculated ?? false,
      is_active: true
    });
    return response.data.data;
  },

  // 批量创建模型字段（单次请求）
  async batchCreateModelFields(modelId: number, fields: Array<{
    field_name: string;
    display_name: string;
    description?: string;
    data_type: string;
    is_calculated?: boolean;
  }>): Promise<ModelField[]> {
    const response = await api.post(`${getApiBase()}/models/${modelId}/fields/batch`, {
      fields
    });
    return response.data.data || [];
  },

  // 更新模型字段
  async updateModelField(fieldId: number, updates: {
    field_name?: string;
    display_name?: string;
    description?: string;
    data_type?: string;
    is_calculated?: boolean;
    is_active?: boolean;
  }): Promise<void> {
    await api.put(`${getApiBase()}/model-fields/${fieldId}`, updates);
  },

  // 删除模型字段
  async deleteModelField(fieldId: number): Promise<void> {
    await api.delete(`${getApiBase()}/model-fields/${fieldId}`);
  },

  // 获取建模详情（包含模型）
  async getModelingWithDetails(projectId: string, version?: string): Promise<ModelingWithDetails> {
    const url = version 
      ? `${getApiBase()}/modeling/${encodeURIComponent(projectId)}/details?version=${version}`
      : `${getApiBase()}/modeling/${encodeURIComponent(projectId)}/details`;
    const response = await api.get(url);
    return response.data.data;
  },

  // 同步模型（调用远程恢复API）
  async syncModel(
    modelId: number,
    modelData: any,
    headers?: { tenantId?: string; projectId?: string }
  ): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      console.log('🔄 开始同步模型:', {
        modelId,
        modelName: modelData.displayName || modelData.referenceName || modelData.model_id
      });

      const requestHeaders: Record<string, string> = {};
      if (headers?.tenantId) requestHeaders['X-Tenant-Id'] = headers.tenantId;
      if (headers?.projectId) requestHeaders['X-Project-Id'] = headers.projectId;

      // restore_single 可能会触发远程“删除/创建/写字段”多步操作
      // 这里把超时放大，避免客户端因为 5 分钟而提前中断
      const axiosConfig: any = { timeout: 900000 }; // 15分钟超时
      if (Object.keys(requestHeaders).length) {
        axiosConfig.headers = requestHeaders;
      }

      // 调用远程恢复API，服务器配置由后端自动获取，增加超时时间到5分钟
      const response = await api.post(`${getApiBase()}/remote_modeling/restore_single`, {
        model: modelData,
        modelIndex: 0,
        totalModels: 1
      }, axiosConfig);
      
      console.log('📡 同步API响应:', response.data);
      
      if (response.data.success) {
        console.log('✅ 模型同步成功:', response.data.message);
        return {
          success: true,
          message: response.data.message,
          data: response.data.data
        };
      } else {
        const detail = response.data?.error || response.data?.message || '同步失败';
        console.warn('⚠️ 模型同步失败:', detail);
        return {
          success: false,
          message: detail
        };
      }
    } catch (error: any) {
      const data = error.response?.data;
      const errorMessage = (data?.error || data?.message || error.message || '同步失败') as string;
      console.error('❌ 同步模型失败:', errorMessage, error);
      
      return {
        success: false,
        message: errorMessage
      };
    }
  }
  ,
  // 从后端导入建模（默认文件或传参指定文件/数据）
  async importModeling(payload: {
    projectId: string;
    filePath?: string;
    modeling?: any;
    models?: any[];
    modelingType?: 'SQLSERVER' | 'HANA';
  }): Promise<{ success: boolean; message?: string; data?: any }> {
    const response = await api.post(`${getApiBase()}/modeling/import`, payload);
    return response.data;
  }
};

